import React, { useState, useMemo } from 'react';
import { cn } from '@core/lib/utils';
import { ThumbsUp, ThumbsDown } from 'lucide-react';
import type { ReactionType, MessageReaction } from '../types';
import { chatConfig, useChatCopy } from '../../config';

interface MessageReactionsProps {
  messageId: string;
  reactions?: MessageReaction[];
  onReact: (messageId: string, reactionType: ReactionType) => void;
  className?: string;
  disabled?: boolean;
}

const REACTION_CONFIG = {
  thumbs_up: {
    icon: ThumbsUp,
    labelKey: 'good',
    color: 'text-muted-foreground',
    activeColor: 'text-foreground',
  },
  thumbs_down: {
    icon: ThumbsDown,
    labelKey: 'bad',
    color: 'text-muted-foreground',
    activeColor: 'text-foreground',
  },
} as const;

export const MessageReactions = React.memo(function MessageReactions({
  messageId,
  reactions = [],
  onReact,
  className,
  disabled = false,
}: MessageReactionsProps) {
  const [hoveredReaction, setHoveredReaction] = useState<ReactionType | null>(null);
  // #2109 R3-B24: labels arrive through the copy pack.
  const reactionsCopy = useChatCopy().bubbleChrome.reactions;
  // #1302 Guard B: the signed-in user’s identity comes from the brand’s auth
  // store, injected as a hook through BrandChatConfig (static per build, so
  // the hook call order is stable).
  const { useCurrentUserEmail } = chatConfig().hooks;
  const currentUserEmail = useCurrentUserEmail();

  // Calculate reaction counts and check if user has reacted
  const reactionStats = useMemo(() => {
    const stats: Record<'thumbs_up' | 'thumbs_down', { count: number; userReacted: boolean }> = {
      thumbs_up: { count: 0, userReacted: false },
      thumbs_down: { count: 0, userReacted: false },
    };

    reactions.forEach((reaction) => {
      if (reaction.type === 'thumbs_up' || reaction.type === 'thumbs_down') {
        stats[reaction.type].count += 1;
        // Check if this reaction belongs to the current user
        if (currentUserEmail && reaction.userEmail === currentUserEmail) {
          stats[reaction.type].userReacted = true;
        }
      }
    });

    return stats;
  }, [reactions, currentUserEmail]);

  // Check if user has any active reaction
  const userActiveReaction = useMemo(() => {
    if (reactionStats.thumbs_up.userReacted) return 'thumbs_up';
    if (reactionStats.thumbs_down.userReacted) return 'thumbs_down';
    return null;
  }, [reactionStats]);

  const handleReactionClick = (reactionType: ReactionType) => {
    if (disabled) return;
    onReact(messageId, reactionType);
  };

  return (
    <div
      className={cn(
        'flex items-center gap-0.5 transition-opacity',
        className
      )}
      role="toolbar"
      aria-label={reactionsCopy.regionLabel}
    >
      {(Object.keys(REACTION_CONFIG) as ('thumbs_up' | 'thumbs_down')[]).map((reactionType) => {
        const config = REACTION_CONFIG[reactionType];
        const stats = reactionStats[reactionType];
        const isActive = stats.userReacted;
        const Icon = config.icon;

        // Hide the non-selected option when one is active (GPT behavior)
        if (userActiveReaction && userActiveReaction !== reactionType) {
          return null;
        }

        return (
          <button
            key={reactionType}
            onClick={() => handleReactionClick(reactionType)}
            onMouseEnter={() => setHoveredReaction(reactionType)}
            onMouseLeave={() => setHoveredReaction(null)}
            disabled={disabled}
            className={cn(
              'relative flex items-center gap-1 px-1.5 py-1 rounded-md',
              'transition-all duration-150',
              'focus:outline-none focus:ring-1 focus:ring-ring',
              'disabled:opacity-50 disabled:cursor-not-allowed',
              // GPT-like subtle styling
              isActive
                ? 'bg-muted/50 border border-border/50'
                : 'border border-transparent hover:border-border/50 hover:bg-muted/30'
            )}
            title={reactionsCopy[config.labelKey]}
            aria-label={reactionsCopy[config.labelKey]}
            aria-pressed={isActive}
          >
            {/* Icon display */}
            <Icon
              className={cn(
                'w-4 h-4 transition-all duration-150',
                isActive ? config.activeColor : config.color,
                hoveredReaction === reactionType && 'scale-110'
              )}
              strokeWidth={1.5}
            />
          </button>
        );
      })}
    </div>
  );
});
